import {
  type MoonPhaseEvent,
  moonState,
  type PhaseName,
  phaseName,
  phasesBetween,
} from "@/lib/tool-kit/astronomy/moon";
import {
  civilFromDayNumber,
  dayNumber,
  localDayNumber,
  zonedToInstant,
} from "@/lib/tool-kit/astronomy/zone";

export interface CalendarDay {
  /** Day of the month, 1-based. */
  day: number;
  /** The Moon at local noon on this day. */
  illumination: number;
  waxing: boolean;
  name: PhaseName;
  /** A principal phase whose exact moment falls on this local day. */
  event: MoonPhaseEvent | null;
}

export interface MonthCalendar {
  /** Empty cells before the 1st when weeks start on Monday (0–6). */
  leadingBlanks: number;
  days: CalendarDay[];
}

/** Monday = 0 … Sunday = 6 for a day number (1970-01-01 was a Thursday). */
export function mondayIndex(days: number): number {
  return (((days + 3) % 7) + 7) % 7;
}

/**
 * The Moon on each day of a month, read at local noon in a zone, with the days on which a
 * new moon, quarter or full moon falls marked by the exact event.
 */
export function monthCalendar(
  year: number,
  month: number,
  timeZone: string
): MonthCalendar {
  const first = dayNumber(year, month, 1);
  const next = dayNumber(
    month === 12 ? year + 1 : year,
    month === 12 ? 1 : month + 1,
    1
  );
  const events = phasesBetween(
    (first - 2) * 86_400_000,
    (next + 2) * 86_400_000
  );
  const days: CalendarDay[] = [];
  for (let d = first; d < next; d += 1) {
    const civil = civilFromDayNumber(d);
    const noon = zonedToInstant(
      { ...civil, hour: 12, minute: 0, second: 0 },
      timeZone
    );
    const state = moonState(noon);
    days.push({
      day: civil.day,
      illumination: state.illumination,
      waxing: state.waxing,
      name: phaseName(state.elongation),
      event:
        events.find((event) => localDayNumber(event.time, timeZone) === d) ??
        null,
    });
  }
  return { leadingBlanks: mondayIndex(first), days };
}
